import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react'
import { MessageKeys, useMessage } from '@opallabs/hooks'
import { useNavigationlessRoute } from '@opallabs/routing'

type NavigationStateContextValue = {
  closeOptionsPopover: () => void
  collapseNavigation: () => void
  expandNavigation: () => void
  hideNavigation: () => void
  navigationExpanded: boolean
  navigationHidden: boolean
  openOptionsPopover: () => void
  optionPopoverExpanded: boolean
  resetNavigation: () => void
  showNavigation: () => void
}

export const NavigationStateContext = createContext<
  NavigationStateContextValue | undefined
>(undefined)
NavigationStateContext.displayName = 'NavigationStateContext'

export const NavigationStateProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const [expanded, setExpanded] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [optionPopoverExpanded, setOptionPopoverExpanded] = useState(false)
  const onNavigationlessRoute = useNavigationlessRoute()

  useMessage(MessageKeys.shroudOpen, () => setHidden(true))
  useMessage(MessageKeys.shroudClose, () => setHidden(false))

  const navigationHidden = useMemo(
    () => (onNavigationlessRoute ? true : hidden),
    [hidden, onNavigationlessRoute]
  )

  const openOptionsPopover = useCallback(
    () => setOptionPopoverExpanded(true),
    []
  )

  const closeOptionsPopover = useCallback(
    () => setOptionPopoverExpanded(false),
    []
  )

  const expandNavigation = useCallback(() => setExpanded(true), [])
  const collapseNavigation = useCallback(() => setExpanded(false), [])
  const showNavigation = useCallback(() => setHidden(false), [])
  const hideNavigation = useCallback(() => setHidden(true), [])

  const resetNavigation = useCallback(() => {
    setHidden(false)
    setExpanded(false)
    setOptionPopoverExpanded(false)
  }, [])

  const value = useMemo(
    () => ({
      closeOptionsPopover,
      collapseNavigation,
      expandNavigation,
      hideNavigation,
      navigationExpanded: expanded,
      navigationHidden,
      openOptionsPopover,
      optionPopoverExpanded,
      resetNavigation,
      showNavigation,
    }),
    [
      closeOptionsPopover,
      collapseNavigation,
      expanded,
      expandNavigation,
      hideNavigation,
      navigationHidden,
      openOptionsPopover,
      optionPopoverExpanded,
      resetNavigation,
      showNavigation,
    ]
  )

  return (
    <NavigationStateContext.Provider value={value}>
      {children}
    </NavigationStateContext.Provider>
  )
}

export const useNavigationState = (): NavigationStateContextValue => {
  const context = useContext(NavigationStateContext)

  if (context === undefined) {
    throw new Error(
      'useNavigationState must be used within a NavigationStateProvider'
    )
  }

  return context
}
